import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/services/api/client';
import {
  buildSsoInitiateUrl,
  isCompleteEmail,
  markSsoAutoEmail,
  sameSsoAutoEmail,
  type SsoDiscoverInfo,
} from '@/utils/ssoRedirect';

type DiscoverResponse = {
  ssoRequired?: boolean;
  organizationId?: string;
  organizationName?: string;
  authMethod?: string;
};

export function useSsoDomainLogin(
  email: string,
  options: {
    enabled: boolean;
    preferPassword: boolean;
    redirectPath: string;
  }
) {
  const { enabled, preferPassword, redirectPath } = options;
  const [sso, setSso] = useState<SsoDiscoverInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [cameBack, setCameBack] = useState(false);
  const redirectingRef = useRef(false);

  const startSso = useCallback(
    (info: SsoDiscoverInfo, hintEmail: string) => {
      if (redirectingRef.current) return;
      redirectingRef.current = true;
      markSsoAutoEmail(hintEmail);
      window.location.replace(
        buildSsoInitiateUrl({
          organizationId: info.organizationId,
          authMethod: info.authMethod,
          email: hintEmail,
          redirect: redirectPath,
        })
      );
    },
    [redirectPath]
  );

  useEffect(() => {
    if (!enabled || preferPassword || !isCompleteEmail(email)) {
      setSso(null);
      setCameBack(false);
      setChecking(false);
      return;
    }

    const trimmed = email.trim();
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setChecking(true);
      try {
        const res = await api.get('/api/auth/federation/discover', {
          params: { email: trimmed },
        });
        if (cancelled) return;
        const data = res.data?.data as DiscoverResponse | undefined;
        const method = data?.authMethod;
        if (
          data?.ssoRequired &&
          data.organizationId &&
          (method === 'OIDC' || method === 'SAML2')
        ) {
          const info: SsoDiscoverInfo = {
            organizationId: String(data.organizationId),
            organizationName: data.organizationName,
            authMethod: method,
          };
          setSso(info);
          if (sameSsoAutoEmail(trimmed)) {
            setCameBack(true);
            return;
          }
          startSso(info, trimmed);
        } else {
          setSso(null);
          setCameBack(false);
        }
      } catch {
        if (!cancelled) {
          setSso(null);
          setCameBack(false);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [email, enabled, preferPassword, startSso]);

  return { sso, checking, cameBack, startSso };
}
